import { getDataPointLabel } from '@modules/common/hooks/useGetBucketLabel/getDataPointLabel';
import {
  getDataPointId,
  getIdOrLabelFromCategory,
  mapEdgeCategoryToCategory,
} from '@modules/common/utils';
import { CompareReportDataPoint } from '@modules/quantify/store/types';
import {
  AggregateBucketKey,
  GetDeltaAggregatesResponseViewFragment,
  IntermediateAggregateDataPoint,
} from 'generated/graphql';
import { round } from 'lodash';
import { EQLFunctionName } from 'v2/core/repository/eql/types';

interface getCompareDataPointsFromAggregatesResponseParams {
  deltaAggregatesResponseView: GetDeltaAggregatesResponseViewFragment;
  whitelistIDs?: string[];
  shouldRemoveFileUploadPrefix: boolean;
}

export function getCompareDataPointsFromAggregatesResponse({
  deltaAggregatesResponseView: result,
  whitelistIDs,
  shouldRemoveFileUploadPrefix,
}: getCompareDataPointsFromAggregatesResponseParams) {
  if (!result) {
    return { aggregateHits: 0, records: [] };
  }

  const getNPS = (
    currIntermediates: IntermediateAggregateDataPoint[] = [],
    prevIntermediates: IntermediateAggregateDataPoint[] = [],
  ) => {
    const curNPS = currIntermediates?.find(
      (intermediate) => intermediate.func === EQLFunctionName.NPS,
    )?.value.nps;

    const prevNPS = prevIntermediates?.find(
      (intermediate) => intermediate.func === EQLFunctionName.NPS,
    )?.value.nps;

    const nps =
      curNPS || prevNPS
        ? {
            currValue: curNPS,
            prevValue: prevNPS,
          }
        : undefined;

    return nps;
  };

  const getCSAT = (
    currIntermediates: IntermediateAggregateDataPoint[] = [],
    prevIntermediates: IntermediateAggregateDataPoint[] = [],
  ) => {
    const curCSAT = currIntermediates?.find(
      (intermediate) => intermediate.func === EQLFunctionName.CSAT,
    )?.value.csat;
    const prevCSAT = prevIntermediates?.find(
      (intermediate) => intermediate.func === EQLFunctionName.CSAT,
    )?.value.csat;
    const csat =
      curCSAT || prevCSAT
        ? {
            currValue: curCSAT,
            prevValue: prevCSAT,
          }
        : undefined;

    return csat;
  };

  const getMean = (
    currIntermediates: IntermediateAggregateDataPoint[] = [],
    prevIntermediates: IntermediateAggregateDataPoint[] = [],
  ) => {
    const curMean = currIntermediates?.find(
      (intermediate) => intermediate.func === EQLFunctionName.Mean,
    )?.value.mean;

    const prevMean = prevIntermediates?.find(
      (intermediate) => intermediate.func === EQLFunctionName.Mean,
    )?.value.mean;

    const mean =
      curMean || prevMean
        ? {
            currValue: curMean,
            prevValue: prevMean,
          }
        : undefined;

    return mean;
  };

  return {
    aggregateHits: result.aggregateHits,
    records: result.dataV2
      .map(({ bucket, __typename, ...rest }) => {
        if (bucket == null) {
          return undefined;
        }

        const aggregateBucket = bucket as AggregateBucketKey;

        const id = getDataPointId(aggregateBucket);
        const category = mapEdgeCategoryToCategory(aggregateBucket.category);
        const isInvisible = aggregateBucket.label?.isVisible === false;
        const isUpdateInProgress =
          Boolean(aggregateBucket.label?.updateInProgress) && !isInvisible;

        if (!!whitelistIDs?.length) {
          const idWithCategory = getIdOrLabelFromCategory({
            key: id,
            category,
          });

          if (
            !(
              whitelistIDs.includes(id) || whitelistIDs.includes(idWithCategory)
            )
          ) {
            return undefined;
          }
        }

        const isPrevValueRelevant = rest.prevIntermediates?.find(
          (intermediate) => intermediate.func === EQLFunctionName.ApplyTaxonomy,
        )?.value?.applyTaxonomy?.isRelevant;

        const isCurrValueRelevant = rest.currIntermediates?.find(
          (intermediate) => intermediate.func === EQLFunctionName.ApplyTaxonomy,
        )?.value?.applyTaxonomy?.isRelevant;

        const dataPoint: CompareReportDataPoint = {
          id,
          label:
            getDataPointLabel({
              bucket: aggregateBucket,
              shouldRemoveFileUploadPrefix,
            }) ?? '',
          category,
          prevValue: round(rest.prevValue, 2),
          currValue: round(rest.currValue, 2),
          relativeDelta: round(rest.relativeDelta, 2),
          absoluteDelta: round(rest.absoluteDelta, 2),
          absValueDelta: Math.abs(round(rest.absoluteDelta, 2)),
          isRelevant: isPrevValueRelevant ?? isCurrValueRelevant ?? true,
          nps: getNPS(rest.currIntermediates, rest.prevIntermediates),
          csat: getCSAT(rest.currIntermediates, rest.prevIntermediates),
          mean: getMean(rest.currIntermediates, rest.prevIntermediates),
          isInvisible,
          isUpdateInProgress,
        };

        return dataPoint;
      })
      .filter((item): item is CompareReportDataPoint => !!item),
  };
}
